/**
 * Adds mobile prev/next arrows to the native product media gallery.
 * Horizon renders slideshow arrows only in "carousel" presentation and hides
 * them on mobile; the reference wants arrows on the mobile slideshow. Rather
 * than change the presentation (which would alter the desktop stacked layout),
 * we add lightweight arrows that drive the existing dot controls
 * (`.slideshow-control` with on:click="/select/N") — reusing Horizon's own
 * navigation. Arrows are shown on mobile only (via CSS).
 *
 * The arrows disable at the ends: prev on the first slide, next on the last —
 * kept in sync with the active dot (updated by swipe or click). The gallery
 * slideshow is rendered with infinite:false so the ends are real.
 */
(function () {
  const CHEVRON_LEFT =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>';
  const CHEVRON_RIGHT =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';

  function activeIndex(dots) {
    return dots.findIndex((b) => b.getAttribute('aria-selected') === 'true');
  }

  function enhance(gallery) {
    if (gallery.dataset.dbNav === '1') return;
    const dots = Array.from(gallery.querySelectorAll('.slideshow-control'));
    if (dots.length < 2) return;
    gallery.dataset.dbNav = '1';

    const make = (cls, label, svg) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'db-gnav ' + cls;
      b.setAttribute('aria-label', label);
      b.innerHTML = svg;
      return b;
    };

    const prev = make('db-gnav--prev', 'Previous image', CHEVRON_LEFT);
    const next = make('db-gnav--next', 'Next image', CHEVRON_RIGHT);

    const sync = () => {
      const i = activeIndex(dots);
      prev.disabled = i <= 0;
      next.disabled = i < 0 || i >= dots.length - 1;
    };

    prev.addEventListener('click', () => {
      const i = activeIndex(dots);
      if (i > 0) dots[i - 1].click();
    });
    next.addEventListener('click', () => {
      const i = activeIndex(dots);
      if (i > -1 && i < dots.length - 1) dots[i + 1].click();
    });

    gallery.appendChild(prev);
    gallery.appendChild(next);

    // Keep the disabled state in sync with the active dot (swipe or click updates
    // aria-selected on the dot controls).
    new MutationObserver(sync).observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
    sync();
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(enhance);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Re-enhance if Horizon re-renders the gallery (e.g. variant change).
  // The dataset guard on each element keeps this cheap; debounce via rAF.
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      init();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
